import { useMemo } from "react"
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import { useGetListBannerDetailQuery, useUpdateBannerMutation } from 'store/serviceBookingEventBanner'
import type { PartialBannerCreateProps, SegmentationFieldsTypes } from 'modules/partial'
import type { DetailStateProps, ErrorProps } from 'modules/types'
import { BannerDialogForm } from 'pages/campaign/event/serviceBooking/banner/create/create'
import { buildSegmentationJson, SEGMENTATION_DELETE_FIELDS } from "components/segmentation/utils"
import { BANNER_PLACEMENT_POLICY } from 'pages/campaign/event/serviceBooking/banner/config'
import type { BannerView } from 'pages/campaign/event/serviceBooking/banner/config'

type EditBannerProps = DetailStateProps & {
    id: string | number
    view: BannerView
    showEventSelector: boolean
}

const Edit = ({ open, id, onClose, view, showEventSelector }: EditBannerProps) => {
    const { data, isFetching, isLoading } = useGetListBannerDetailQuery(id, { skip: !open })

    const [updateBanner, update] = useUpdateBannerMutation()
    const placement = view.placement

    const initialValues: PartialBannerCreateProps = useMemo(() => {
        const segment: SegmentationFieldsTypes = JSON.parse(data?.segmentationValue || '{}')

        return {
            Image: {} as File,
            data: {
                id: (data && data.id) || 0,
                bannerImage: data?.bannerImage || '',
                Placement: placement,
                EventId: data?.eventId || undefined,
                ValidFrom: (data && data.validFrom) || '',
                ValidUntil: (data && data.validUntil) || '',
                Status: (data && data.status) || '',
                targetOptions: data?.segmentationType
                    ? data.segmentationType.split(',').map((s: string) => s.trim())
                    : [],
                memberTiers: (segment && segment.memberTiers) || [],
                carModel: (segment && segment.carModel) || [],
                CarModelYears: (segment && segment.CarModelYears) || [],
                carOwnership: (segment && segment.carOwnership) || [],
                salutation: (segment && segment.salutation) || '',
                vinCsvFile: null,
                memberIdCsvFile: null,
                vinCsvFileUrl: data && segment.vinCsvFileUrl,
                memberIdCsvFileUrl: data && segment.memberIdCsvFileUrl,
                locations: (segment && segment.locations) || [],
                licensePlateType: (segment && segment.licensePlateType) || '',
                activeStatus: (segment && segment.activeStatus) || '',
                bluelinkStatus: (segment && segment.bluelinkStatus) || '',
                segmentationType: data?.segmentationType,
                segmentationValue: data?.segmentationValue,
                ...(view.kind === 'banner' ? {
                    Title: data?.title,
                    Url: data?.url
                } : undefined),
                Order: data?.order || 0
            }
        }
    }, [data, view, placement])

    function handleSubmit(values: PartialBannerCreateProps) {
        const formData = new FormData()

        const requestData: Record<string, unknown> = { ...values.data }
        const hasSegmentation = BANNER_PLACEMENT_POLICY[placement].segmentation

        requestData.Placement = placement
        if (!values.data.EventId) delete requestData.EventId

        if (hasSegmentation) {
            requestData.segmentationType = Array.isArray(values.data.targetOptions)
                ? values.data.targetOptions.join(', ')
                : values.data.targetOptions
            requestData.segmentationValue = buildSegmentationJson(values.data)
        } else {
            delete requestData.targetOptions
            if (placement !== 'CustomerCare') {
                delete requestData.segmentationType
                delete requestData.segmentationValue
            }
        }
        SEGMENTATION_DELETE_FIELDS.forEach((field) => delete requestData[field])

        if (view.kind === 'service-event' ? values.Image : values.Image instanceof File) {
            formData.append('Image', values.Image)
        }
        if (hasSegmentation && values.data.vinCsvFile) formData.append('file', values.data.vinCsvFile)
        if (hasSegmentation && values.data.memberIdCsvFile) formData.append('file', values.data.memberIdCsvFile)
        formData.append('data', JSON.stringify(requestData))
        updateBanner(formData)
    }

    if (!open) return <></>

    return (
        <>
            <BannerDialogForm
                open={open}
                onClose={onClose}
                loading={update.isLoading}
                fetching={isFetching || isLoading}
                initialValues={initialValues}
                view={view}
                showEventSelector={showEventSelector}
                onSubmit={handleSubmit}
            />
            <Notification
                open={!update.isLoading && !update.isUninitialized}
                onClose={() => (update.isError ? update.reset() : location.reload())}
                isError={Boolean(update.error) && update.isError}
                message={GLOBAL.returnExceptionMessage(update.isError, update.error as ErrorProps)}
            />
        </>
    )
}

export default Edit
